import { useState } from 'react'
import {
  Button,
  Card,
  Select,
  Space,
  DatePicker,
  Typography,
  Divider,
  message,
  Spin,
  Alert,
} from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface CashFlowData {
  operating: number
  investing: number
  financing: number
  net_change: number
  opening_balance: number
  closing_balance: number
}

function AmountText({ value }: { value: number }) {
  const color = value >= 0 ? '#52c41a' : '#ff4d4f'
  const formatted = `₹ ${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
  const sign = value < 0 ? '(' : ''
  const signEnd = value < 0 ? ')' : ''
  return (
    <Text strong style={{ color, fontSize: 15 }}>
      {sign}{formatted}{signEnd}
    </Text>
  )
}

function StatementRow({
  label,
  value,
  bold,
  indent,
  dividerAbove,
}: {
  label: string
  value: number | null
  bold?: boolean
  indent?: boolean
  dividerAbove?: boolean
}) {
  return (
    <>
      {dividerAbove && <Divider style={{ margin: '8px 0' }} />}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 0',
          paddingLeft: indent ? 24 : 0,
        }}
      >
        <Text strong={bold} style={{ fontSize: bold ? 14 : 13 }}>
          {label}
        </Text>
        {value != null ? (
          bold ? (
            <AmountText value={value} />
          ) : (
            <Text style={{ fontSize: 13 }}>
              ₹ {value.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </Text>
          )
        ) : (
          <Text type="secondary">—</Text>
        )}
      </div>
    </>
  )
}

export default function CashFlowPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ])
  const [fetchEnabled, setFetchEnabled] = useState(false)

  const cashFlowQuery = useQuery<CashFlowData>({
    queryKey: [
      'mis-cash-flow',
      plantId,
      dateRange[0]?.format('YYYY-MM-DD'),
      dateRange[1]?.format('YYYY-MM-DD'),
    ],
    queryFn: async () => {
      const res = await apiClient.get('/mis/cash-flow', {
        params: {
          plant_id: plantId,
          from_date: dateRange[0].format('YYYY-MM-DD'),
          to_date: dateRange[1].format('YYYY-MM-DD'),
        },
      })
      return res.data
    },
    enabled: !!plantId && fetchEnabled,
  })

  const handleGenerate = () => {
    if (!plantId) {
      message.warning('Please select a plant')
      return
    }
    if (!dateRange[0] || !dateRange[1]) {
      message.warning('Please select a date range')
      return
    }
    setFetchEnabled(true)
    cashFlowQuery.refetch()
  }

  const data = cashFlowQuery.data

  return (
    <>
      <PageHeader
        title="Cash Flow Statement"
        subtitle="Track cash inflows and outflows across operating, investing, and financing activities"
        extra={
          <Space wrap>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              placeholder="Select plant"
              options={plants?.map((p: { id: string; name: string }) => ({
                value: p.id,
                label: p.name,
              }))}
            />
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]])
                  setFetchEnabled(false)
                }
              }}
              format="DD-MM-YYYY"
            />
            <Button type="primary" onClick={handleGenerate} loading={cashFlowQuery.isFetching}>
              Generate
            </Button>
          </Space>
        }
      />

      <div style={{ padding: '0 24px 24px' }}>
        {cashFlowQuery.isLoading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" tip="Generating cash flow statement..." />
          </div>
        )}

        {cashFlowQuery.isError && (
          <Alert
            type="error"
            message="Failed to load cash flow data"
            description="Please check the selected plant and date range and try again."
            showIcon
          />
        )}

        {data && !cashFlowQuery.isLoading && (
          <Card
            style={{ maxWidth: 720 }}
            styles={{ body: { padding: '24px 32px' } }}
          >
            {/* Statement header */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 4 }}>
                CASH FLOW STATEMENT
              </Title>
              <Text type="secondary">
                {dateRange[0].format('DD MMM YYYY')} — {dateRange[1].format('DD MMM YYYY')}
              </Text>
            </div>

            <Divider style={{ margin: '0 0 8px' }} />

            <StatementRow
              label="A.  Cash from Operating Activities"
              value={data.operating}
              bold
            />
            <StatementRow
              label="B.  Cash from Investing Activities"
              value={data.investing}
              bold
            />
            <StatementRow
              label="C.  Cash from Financing Activities"
              value={data.financing}
              bold
            />

            <Divider style={{ margin: '8px 0' }} />

            <StatementRow
              label="Net Change in Cash (A + B + C)"
              value={data.net_change}
              bold
              dividerAbove={false}
            />

            <div style={{ height: 8 }} />

            <StatementRow
              label="Opening Cash Balance"
              value={data.opening_balance}
              indent
            />
            <StatementRow
              label="Closing Cash Balance"
              value={data.closing_balance}
              bold
              indent
            />

            <Divider style={{ margin: '12px 0 16px' }} />

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => message.info('Print coming soon')}
              >
                Print
              </Button>
            </div>
          </Card>
        )}

        {!data && !cashFlowQuery.isLoading && !cashFlowQuery.isError && (
          <Card style={{ maxWidth: 720 }}>
            <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
              <Text type="secondary">
                Select a plant and date range, then click "Generate" to view the cash flow
                statement.
              </Text>
            </div>
          </Card>
        )}
      </div>
    </>
  )
}
